import React, { useState } from "react";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Sidebar } from "primereact/sidebar";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import toast from "react-hot-toast";

import {
  useGetPhotoReportsQuery,
  useGetPhotoReportDetailQuery,
  useDeletePhotoReportMutation,
  useRestorePhotoReportMutation,
} from "../../store/services/photo-reports";
import {
  PhotoReportListItem,
  PhotoReportReason,
  PhotoReportStatus,
  photoReportReasonLabels,
  photoReportStatusLabels,
} from "../../models/photoReports";

const statusOptions: { label: string; value: PhotoReportStatus }[] = [
  { label: "Pendiente", value: "pending" },
  { label: "Restaurada", value: "restored" },
  { label: "Eliminada", value: "deleted" },
];

const reasonOptions: { label: string; value: PhotoReportReason }[] = [
  { label: "Inapropiado", value: "inappropriate" },
  { label: "Falso/Suplantación", value: "fake" },
  { label: "Otro", value: "other" },
];

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

function getAgeMs(createdAt: string): number {
  const ts = new Date(createdAt).getTime();
  if (Number.isNaN(ts)) return 0;
  return Date.now() - ts;
}

function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function getSlaSeverity(
  ageMs: number,
  status: PhotoReportStatus
): "danger" | "warning" | "success" | "info" {
  if (status !== "pending") return "info";
  if (ageMs >= TWELVE_HOURS_MS) return "danger";
  if (ageMs >= SIX_HOURS_MS) return "warning";
  return "success";
}

export const PhotoReports: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [status, setStatus] = useState<PhotoReportStatus | undefined>("pending");
  const [reason, setReason] = useState<PhotoReportReason | undefined>(undefined);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hdPhotoUrl, setHdPhotoUrl] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetPhotoReportsQuery({
    page,
    pageSize,
    status,
    reason,
  });

  const [deletePhotoReport, { isLoading: isDeleting }] = useDeletePhotoReportMutation();
  const [restorePhotoReport, { isLoading: isRestoring }] = useRestorePhotoReportMutation();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handlePage = (e: DataTablePageEvent) => {
    setPage((e.page ?? 0) + 1);
    setPageSize(e.rows);
  };

  const photoBody = (row: PhotoReportListItem) => {
    const url = row.userImage?.photoUrl;
    if (!url) {
      return (
        <div
          className="flex align-items-center justify-content-center bg-gray-200 border-round"
          style={{ width: 56, height: 56 }}
        >
          <i className="pi pi-image text-400" />
        </div>
      );
    }
    return (
      <img
        src={url}
        alt="reported"
        onClick={() => setHdPhotoUrl(row.userImage?.photoUrlHd ?? url)}
        style={{
          width: 56,
          height: 56,
          objectFit: "cover",
          borderRadius: 8,
          cursor: "zoom-in",
          border: "1px solid #e5e7eb",
        }}
      />
    );
  };

  const reportedUserBody = (row: PhotoReportListItem) => {
    const u = row.reportedUser;
    if (!u) return <span className="text-color-secondary">#{row.reportedUserId}</span>;
    return (
      <div className="flex flex-column">
        <span className="font-semibold">{u.firstName} {u.lastName}</span>
        <span className="text-xs text-color-secondary">{u.email}</span>
      </div>
    );
  };

  const reporterUserBody = (row: PhotoReportListItem) => {
    const u = row.reporterUser;
    if (!u) return <span className="text-color-secondary">#{row.reporterUserId}</span>;
    return (
      <div className="flex flex-column">
        <span>{u.firstName} {u.lastName}</span>
        <span className="text-xs text-color-secondary">{u.email}</span>
      </div>
    );
  };

  const reasonBody = (row: PhotoReportListItem) => (
    <Tag value={photoReportReasonLabels[row.reason]} severity="info" />
  );

  const statusBody = (row: PhotoReportListItem) => {
    const sev =
      row.status === "pending"
        ? "warning"
        : row.status === "deleted"
        ? "danger"
        : "success";
    return <Tag value={photoReportStatusLabels[row.status]} severity={sev as any} />;
  };

  const slaBody = (row: PhotoReportListItem) => {
    const age = getAgeMs(row.createdAt);
    const severity = getSlaSeverity(age, row.status);
    const value = formatAge(age);
    return <Tag value={value} severity={severity} />;
  };

  const deletionCountBody = (row: PhotoReportListItem) => {
    const count = row.userImage?.deletionCount ?? 0;
    if (count === 0) return <span className="text-color-secondary">0</span>;
    const sev = count >= 3 ? "danger" : count >= 1 ? "warning" : "info";
    return <Tag value={`${count}`} severity={sev as any} />;
  };

  const actionsBody = (row: PhotoReportListItem) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        text
        severity="info"
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setSelectedId(row.id)}
      />
      {row.userImage?.photoUrlHd && (
        <Button
          icon="pi pi-search-plus"
          rounded
          text
          severity="secondary"
          tooltip="Ver foto HD"
          tooltipOptions={{ position: "top" }}
          onClick={() => setHdPhotoUrl(row.userImage?.photoUrlHd ?? null)}
        />
      )}
    </div>
  );

  const header = (
    <div className="grid">
      <div className="col-12 sm:col-6 lg:col-4 my-3">
        <Dropdown
          value={status}
          onChange={(e) => {
            setStatus(e.value || undefined);
            setPage(1);
          }}
          options={statusOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrar por estado"
          showClear
          className="w-full"
        />
      </div>
      <div className="col-12 sm:col-6 lg:col-4 my-3">
        <Dropdown
          value={reason}
          onChange={(e) => {
            setReason(e.value || undefined);
            setPage(1);
          }}
          options={reasonOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrar por motivo"
          showClear
          className="w-full"
        />
      </div>
      <div className="col-12 sm:col-6 lg:col-4 my-3 flex justify-content-end align-items-center">
        <Button
          icon="pi pi-refresh"
          label="Actualizar"
          outlined
          onClick={() => refetch()}
          loading={isFetching}
        />
      </div>
    </div>
  );

  return (
    <div className="p-card gap-4 p-3">
      <ConfirmDialog />
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-4">
        <h1 className="text-center m-0">Moderación de fotos</h1>
      </div>
      <p className="text-center text-color-secondary mb-4 mt-0">
        Cola FIFO de denuncias. Casos resaltados en amarillo (&gt;6h) y rojo (&gt;12h) requieren atención inmediata.
      </p>

      <DataTable
        value={items}
        loading={isLoading || isFetching}
        lazy
        paginator
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={total}
        onPage={handlePage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No hay denuncias en este filtro"
        header={header}
        stripedRows
        dataKey="id"
        rowClassName={(row: PhotoReportListItem) => {
          const age = getAgeMs(row.createdAt);
          if (row.status !== "pending") return "";
          if (age >= TWELVE_HOURS_MS) return "bg-red-50";
          if (age >= SIX_HOURS_MS) return "bg-yellow-50";
          return "";
        }}
      >
        <Column header="#" body={(r: PhotoReportListItem) => <span>#{r.id}</span>} style={{ width: 70 }} />
        <Column header="Foto" body={photoBody} style={{ width: 80 }} />
        <Column header="Usuario reportado" body={reportedUserBody} />
        <Column header="Reincidencias" body={deletionCountBody} style={{ width: 120 }} />
        <Column header="Motivo" body={reasonBody} style={{ width: 180 }} />
        <Column header="Estado" body={statusBody} style={{ width: 120 }} />
        <Column header="Antigüedad" body={slaBody} style={{ width: 120 }} />
        <Column header="Reportador" body={reporterUserBody} />
        <Column header="Acciones" body={actionsBody} style={{ width: 140 }} />
      </DataTable>

      <ReportDetailSidebar
        reportId={selectedId}
        onHide={() => setSelectedId(null)}
        onOpenHd={(url) => setHdPhotoUrl(url)}
        onDelete={(id, deletionCount) => {
          confirmDialog({
            header: "Eliminar foto y sancionar usuario",
            message: (
              <div className="flex flex-column gap-2">
                <span>
                  Estás por <b>eliminar la foto</b> del usuario reportado y registrar una sanción.
                </span>
                <span>
                  Sanciones acumuladas actuales: <b>{deletionCount}</b>. Tras esta acción serán <b>{deletionCount + 1}</b>.
                </span>
                <span className="text-red-600 font-semibold">
                  Esta acción no se puede deshacer fácilmente. ¿Continuar?
                </span>
              </div>
            ) as any,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí, eliminar",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: async () => {
              try {
                await deletePhotoReport(id).unwrap();
                toast.success("Foto eliminada y usuario sancionado");
                setSelectedId(null);
                refetch();
              } catch (err: any) {
                toast.error(err?.data?.error?.message || "No se pudo eliminar la foto");
              }
            },
          });
        }}
        onRestore={(id) => {
          confirmDialog({
            header: "Restaurar foto",
            message: "Se descartará la denuncia y la foto seguirá visible. ¿Confirmás?",
            icon: "pi pi-info-circle",
            acceptLabel: "Sí, restaurar",
            rejectLabel: "Cancelar",
            accept: async () => {
              try {
                await restorePhotoReport(id).unwrap();
                toast.success("Denuncia descartada");
                setSelectedId(null);
                refetch();
              } catch (err: any) {
                toast.error(err?.data?.error?.message || "No se pudo restaurar");
              }
            },
          });
        }}
        isMutating={isDeleting || isRestoring}
      />

      <Dialog
        visible={!!hdPhotoUrl}
        onHide={() => setHdPhotoUrl(null)}
        header="Foto en HD"
        style={{ width: "min(720px, 90vw)" }}
        dismissableMask
      >
        {hdPhotoUrl && (
          <img
            src={hdPhotoUrl}
            alt="hd"
            style={{ width: "100%", height: "auto", borderRadius: 8 }}
          />
        )}
      </Dialog>
    </div>
  );
};

interface ReportDetailSidebarProps {
  reportId: number | null;
  onHide: () => void;
  onOpenHd: (url: string) => void;
  onDelete: (id: number, deletionCount: number) => void;
  onRestore: (id: number) => void;
  isMutating: boolean;
}

const ReportDetailSidebar: React.FC<ReportDetailSidebarProps> = ({
  reportId,
  onHide,
  onOpenHd,
  onDelete,
  onRestore,
  isMutating,
}) => {
  const visible = reportId !== null;
  const { data, isLoading, isFetching } = useGetPhotoReportDetailQuery(
    reportId as number,
    { skip: reportId === null }
  );

  const deletionCount = data?.userImage?.deletionCount ?? 0;
  const status = data?.report.status;

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: "min(520px, 95vw)" }}
    >
      <div className="flex flex-column gap-3">
        <h2 className="m-0">Denuncia #{reportId}</h2>
        {(isLoading || isFetching) && (
          <div className="text-color-secondary">Cargando…</div>
        )}
        {data && (
          <>
            <Section title="Estado">
              <div className="flex gap-2 align-items-center">
                <Tag
                  value={photoReportStatusLabels[data.report.status]}
                  severity={
                    data.report.status === "pending"
                      ? "warning"
                      : data.report.status === "deleted"
                      ? "danger"
                      : "success"
                  }
                />
                <Tag
                  value={photoReportReasonLabels[data.report.reason]}
                  severity="info"
                />
              </div>
            </Section>

            <Section title="Foto reportada">
              {data.userImage?.photoUrl ? (
                <img
                  src={data.userImage.photoUrl}
                  alt="reported"
                  onClick={() =>
                    data.userImage?.photoUrlHd && onOpenHd(data.userImage.photoUrlHd)
                  }
                  style={{
                    width: 200,
                    height: 200,
                    objectFit: "cover",
                    borderRadius: 12,
                    cursor: "zoom-in",
                  }}
                />
              ) : (
                <div className="text-color-secondary">Sin imagen disponible</div>
              )}
            </Section>

            <Section title="Usuario reportado">
              {data.reportedUser ? (
                <div className="flex flex-column">
                  <span className="font-semibold">
                    {data.reportedUser.firstName} {data.reportedUser.lastName}
                  </span>
                  <span className="text-color-secondary">{data.reportedUser.email}</span>
                  <span className="text-xs text-color-secondary">
                    ID #{data.reportedUser.id}
                  </span>
                </div>
              ) : (
                <span className="text-color-secondary">
                  Usuario #{data.report.reportedUserId} (no encontrado)
                </span>
              )}
            </Section>

            <Section title="Histórico de sanciones">
              <div className="flex align-items-center gap-2">
                <Tag
                  value={`${deletionCount}`}
                  severity={
                    deletionCount >= 3
                      ? "danger"
                      : deletionCount >= 1
                      ? "warning"
                      : "info"
                  }
                />
                <span className="text-color-secondary">
                  fotos eliminadas previamente por moderación
                </span>
              </div>
              {deletionCount >= 2 && (
                <div className="mt-2 p-2 border-round bg-red-50 text-red-700 text-sm">
                  ⚠ Usuario reincidente. Considerar baneo permanente.
                </div>
              )}
            </Section>

            <Section title="Reportador">
              {data.reporterUser ? (
                <div className="flex flex-column">
                  <span>
                    {data.reporterUser.firstName} {data.reporterUser.lastName}
                  </span>
                  <span className="text-color-secondary">{data.reporterUser.email}</span>
                </div>
              ) : (
                <span className="text-color-secondary">
                  Usuario #{data.report.reporterUserId} (no encontrado)
                </span>
              )}
            </Section>

            {data.report.comment && (
              <Section title="Comentario del reportador">
                <InputTextarea
                  value={data.report.comment}
                  readOnly
                  rows={3}
                  className="w-full"
                />
              </Section>
            )}

            <Section title="Fechas">
              <div className="text-sm text-color-secondary">
                Creada: {new Date(data.report.createdAt).toLocaleString("es-AR")}
              </div>
              {data.report.resolvedAt && (
                <div className="text-sm text-color-secondary">
                  Resuelta: {new Date(data.report.resolvedAt).toLocaleString("es-AR")}
                </div>
              )}
            </Section>

            {status === "pending" && (
              <div className="flex gap-2 mt-3">
                <Button
                  label="Eliminar foto"
                  icon="pi pi-trash"
                  severity="danger"
                  loading={isMutating}
                  onClick={() => onDelete(data.report.id, deletionCount)}
                />
                <Button
                  label="Descartar denuncia"
                  icon="pi pi-check"
                  severity="success"
                  outlined
                  loading={isMutating}
                  onClick={() => onRestore(data.report.id)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Sidebar>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex flex-column gap-2">
    <div className="text-xs font-semibold uppercase text-color-secondary">
      {title}
    </div>
    <div>{children}</div>
  </div>
);
